<script setup lang="ts">
import { computed, inject, onMounted, ref, toRefs, watch } from 'vue';
import { useRoute } from 'vue-router';
import {
  DgrIcon,
  DgrLoading,
  DgrToggleButton,
} from '@stockmarkteam/donguri-ui';
import AutoResizeTextarea from '@/components/survey/input/auto-resize-textarea.vue';
import SurveyFileSpecification from '@/components/survey/input/survey-file-specification.vue';
import SurveyTargetSourceFilter from '@/components/survey/input/survey-target-source-filter.vue';
import {
  DocType,
  FileNameSearchResponse,
  IdentifyValue,
  SearchScope,
  SpecifiedMethod,
  SurveyInputValue,
  SurveyPageName,
  TagType,
} from '@/types';
import { useEnterKeyHandler } from '@/utils/composables/useEnterKeyHandler';
import { useOutsideClick } from '@/utils/composables/useOutsideClick';
import { useSurveyExecutionStatus } from '@/utils/composables/useSurveyExecutionStatus';
import { getRubyStringLength } from '@/utils/getRubyStringLength';
import { specifiedMethodKey } from '@/utils/injectionKeys';
import { getDocTypesQueryParam } from '@/utils/survey/common';
import {
  DEEP_MODE_MAX_CONCURRENCY_MESSAGE,
  SURVEY_QUESTION_MAX_LENGTH,
} from '@/utils/survey/constants';
import SurveySelectedFilters from './survey-selected-filters.vue';
import { featureFlags } from '@/featureFlags';

interface Props {
  sessionId?: string; // surveysテーブルのuuidカラムの値 (要約画面のURLに含まれるID)。深掘り検索（同じスレッド）の間は同じID
  enableTransformInput?: boolean;
  isLoading?: boolean;
  initialSearchScope?: SearchScope;
  initialDocTypes?: Array<DocType | 'user_document'>;
  initialSpecifiedFiles?: Array<FileNameSearchResponse>;
  initialGroupId?: number;
  initialGroupTagIds?: number[];
  initialQuestion?: string;
  initialDeepMode?: boolean;
  pageName: SurveyPageName;
}

/**
 * Props
 */

const props = withDefaults(defineProps<Props>(), {
  enableTransformInput: false,
  isLoading: false,
  initialSearchScope: 'all',
  initialDocTypes: () => [],
  initialSpecifiedFiles: () => [],
  initialGroupTagIds: () => [],
  initialQuestion: '',
  initialDeepMode: false,
});

/**
 * Emits
 */

const emit = defineEmits<{
  submit: [SurveyInputValue];
}>();

/**
 * Local State
 */

const route = useRoute();

// フォームの値と操作
const textareaRef = ref<HTMLTextAreaElement>();
const inputText = ref<string>('');
const {
  initialSearchScope,
  initialDocTypes,
  initialSpecifiedFiles,
  initialGroupId,
  initialGroupTagIds,
  initialQuestion,
} = toRefs(props);
const searchScope = ref<SearchScope>(props.initialSearchScope);
const docTypes = ref<Array<DocType | 'user_document'>>(props.initialDocTypes);
const specifiedFiles = ref<Array<FileNameSearchResponse>>(
  props.initialSpecifiedFiles,
);
const groupId = ref<number | undefined>(props.initialGroupId);
const groupTagIds = ref<number[] | undefined>(props.initialGroupTagIds);
const specifiedMethod = inject(
  specifiedMethodKey,
  ref<SpecifiedMethod | undefined>(undefined),
);
const deepMode = ref(props.initialDeepMode);

const textareaPlaceholder = computed(() =>
  route.name === 'surveyTop'
    ? '質問を入力するかプロンプトテンプレートを試してください'
    : 'キーワードを追加してさらに探索',
);
// NOTE: ruby-grapeのmax_lengthのバリデーションで使用するRubyのString#lengthメソッドに合わせる
const inputTextLength = computed(() => getRubyStringLength(inputText.value));
const isValidInputTextLength = computed(
  () => inputTextLength.value <= SURVEY_QUESTION_MAX_LENGTH,
);
const enableSubmitButton = computed(() => {
  if (props.isLoading) return false;
  if (!inputText.value.trim() || !isValidInputTextLength.value) return false;
  if (isCurrentSurveyInProgress.value) return false;
  if (isDeepModeConcurrencyLimitReached.value) return false;

  return true;
});

// props.enableTransformInputがtrueの場合の、"生成"ボタンの位置調整に関する処理
const isTextareaFocused = ref<boolean>(false);
const containerRef = ref<HTMLDivElement>();
const submitButtonRef = ref<HTMLButtonElement>();
const isClickedOutsideInput = useOutsideClick([
  containerRef,
  '[id="dialog:file-name-search-modal:content"]',
]);
const isClickedOutsideSubmitButton = useOutsideClick([submitButtonRef]);
const shouldButtonChangePosition = computed(() => {
  if (inputText.value) return false;
  // 絞り込みフィルターをクリックした際に、インプットが縮まらないようにするための考慮
  if (!isClickedOutsideInput.value && isClickedOutsideSubmitButton.value)
    return false;

  return props.enableTransformInput && !isTextareaFocused.value;
});

// 各スレッドのsurvey実行状況
const { canStartNewDeepMode, isSurveyInProgress, refreshStatus } =
  useSurveyExecutionStatus();

const isCurrentSurveyInProgress = computed(() => {
  if (!props.sessionId) return false;
  return isSurveyInProgress(props.sessionId);
});

const isDeepModeConcurrencyLimitReached = computed(
  () => !canStartNewDeepMode.value && deepMode.value,
);

watch(
  initialSearchScope,
  newSearchScope => (searchScope.value = newSearchScope),
);
watch(initialDocTypes, newDocTypes => (docTypes.value = newDocTypes));
watch(
  initialSpecifiedFiles,
  newSpecifiedFiles => (specifiedFiles.value = newSpecifiedFiles),
);
watch(initialGroupId, newGroupId => (groupId.value = newGroupId));
watch(
  initialGroupTagIds,
  newGroupTagIds => (groupTagIds.value = newGroupTagIds),
);
watch(
  () => initialQuestion.value,
  newQuestion => (inputText.value = newQuestion),
);
watch(
  () => props.initialDeepMode,
  newDeepMode => (deepMode.value = newDeepMode),
);

/**
 * Handlers
 */
const sendInput = async () => {
  if (props.isLoading) return; // survey実行中の場合は、新規でsurveyを開始させないようにイベントを発火しない
  if (!inputText.value.trim()) return;
  if (isCurrentSurveyInProgress.value) return;

  // ディープモードの場合、最新状態で再度チェック
  if (deepMode.value) {
    refreshStatus();
    if (!canStartNewDeepMode.value) return;
  }

  // 空行が2行以上続く場合は1行にまとめる
  const trimmedInputText = inputText.value.replace(/\n{3,}/g, '\n\n');
  if (inputText.value !== trimmedInputText) {
    inputText.value = trimmedInputText;
  }

  emit('submit', {
    question: inputText.value,
    filterInfo: {
      searchScope: searchScope.value,
      docTypes: docTypes.value,
      specifiedFiles: specifiedFiles.value,
      groupId: groupId.value,
      groupTags: groupTagIds.value,
    },
    deepMode: deepMode.value,
  });
  inputText.value = '';
  if (textareaRef.value) {
    textareaRef.value.style.height = 'auto';
    textareaRef.value?.blur();
  }
  handleBlur();
  if (props.enableTransformInput) isClickedOutsideInput.value = true; // 入力欄が変形可能な場合は、submit時に入力欄を縮める
};
const { handleKeyDown, handleCompositionStart, handleCompositionEnd } =
  useEnterKeyHandler({ sendCallback: sendInput });

const resetInput = () => {
  searchScope.value = 'all';
  groupId.value = undefined;
  groupTagIds.value = undefined;
};

const deleteTag = (value: {
  filterType: TagType;
  identifyValue: IdentifyValue;
}) => {
  switch (value.filterType) {
    case 'docTypes':
      docTypes.value = docTypes.value.filter(
        docType => docType !== value.identifyValue,
      );

      if (docTypes.value.length === 0) {
        resetInput();
      }
      break;
    case 'specifiedFiles': {
      const file = value.identifyValue as FileNameSearchResponse;
      specifiedFiles.value = specifiedFiles.value.filter(f => f.id !== file.id);
      if (specifiedFiles.value.length === 0) {
        specifiedMethod.value = undefined;
        resetInput();
      }
      break;
    }
    case 'groupId':
      // グループが削除されたら、searchScope、docTypes、groupTagIdsもリセットする
      resetInput();
      docTypes.value = [];
      break;
    case 'groupTags':
      if (!groupTagIds.value) return;
      groupTagIds.value = groupTagIds.value.filter(
        id => id !== (value.identifyValue as number),
      );
  }
};

const applyFilter = (value: {
  searchScope: SearchScope;
  docTypes: string[];
  groupId?: number;
  groupTagIds?: number[];
}) => {
  searchScope.value = value.searchScope;
  docTypes.value = value.docTypes as Array<DocType | 'user_document'>;

  // 「検索対象絞り込み」と「ファイル指定」は排他的に選択する仕様のため、docTypesが選択された場合、specified_filesをリセット
  if (value.docTypes.length > 0 && specifiedFiles.value.length > 0) {
    specifiedFiles.value = [];
    specifiedMethod.value = undefined;
  }

  if (value.searchScope === 'group_marks') {
    groupId.value = value.groupId;
    groupTagIds.value = value.groupTagIds;
  } else {
    // searchScopeがgroup_marksではないときはリセット
    groupId.value = undefined;
    groupTagIds.value = [];
  }
};

const resetFilter = () => {
  if (docTypes.value.length > 0) {
    searchScope.value = 'all';
    docTypes.value = [];
    groupId.value = undefined;
    groupTagIds.value = [];
  }
};

const selectFile = () => {
  searchScope.value = 'specified_files';
  docTypes.value = [];
  groupId.value = undefined;
  groupTagIds.value = [];
};

const handleFocus = () => {
  isTextareaFocused.value = true;
};

const handleBlur = () => {
  isTextareaFocused.value = false;
};

onMounted(async () => {
  refreshStatus();

  // 入力欄が変形可能な場合は、初期値をtrueにして縮めておく
  if (props.enableTransformInput) isClickedOutsideInput.value = true;

  const textarea = document.querySelector('textarea');
  if (textarea) {
    textareaRef.value = textarea as HTMLTextAreaElement;
  }

  if (route.name === 'surveySession') {
    // Topや検索から遷移した際に、絞り込みの内容を引き継ぐ
    docTypes.value = getDocTypesQueryParam(route.query.docTypes);
  }

  if (route.name === 'surveyTop' && textareaRef.value) {
    textareaRef.value.focus();
  }
});
</script>

<template>
  <div
    ref="containerRef"
    class="container"
    :class="{ 'fixed-bottom': props.enableTransformInput }"
  >
    <div
      class="input-section"
      :class="{ 'no-filters': shouldButtonChangePosition }"
    >
      <AutoResizeTextarea
        class="c-text-regular--m"
        v-model="inputText"
        :max-height="300"
        :placeholder="textareaPlaceholder"
        :rows="1"
        @keydown="handleKeyDown"
        @compositionstart="handleCompositionStart"
        @compositionend="handleCompositionEnd"
        @focus="handleFocus"
        @blur="handleBlur"
      />
      <template v-if="featureFlags.ANDEV_10336_RAISE_SURVEY_PROMPT_LIMIT">
        <div
          v-if="!shouldButtonChangePosition"
          class="input-footer c-text-regular--m"
        >
          <span v-if="!isValidInputTextLength" class="validation-error-message">
            クエリの最大文字数は{{ SURVEY_QUESTION_MAX_LENGTH }}文字です
          </span>
          <span
            class="character-count"
            :class="{ 'validation-error-message': !isValidInputTextLength }"
          >
            {{ inputTextLength }} / {{ SURVEY_QUESTION_MAX_LENGTH }}
          </span>
        </div>
      </template>
      <div
        v-else-if="!isValidInputTextLength"
        class="input-footer validation-error-message"
      >
        <span class="c-text-regular--m">
          クエリの最大文字数は{{ SURVEY_QUESTION_MAX_LENGTH }}文字です
        </span>
      </div>
    </div>
    <div
      class="filter-submit-section"
      :class="{
        'hidden-filter-submit-section': shouldButtonChangePosition,
      }"
    >
      <div v-if="!shouldButtonChangePosition">
        <div class="search-options">
          <SurveyTargetSourceFilter
            :search-scope="searchScope"
            :doc-types="docTypes"
            :group-id="groupId"
            :group-tag-ids="groupTagIds"
            @apply-filter="applyFilter"
            @reset-filter="resetFilter"
          />
          <SurveyFileSpecification
            v-model:specified-files="specifiedFiles"
            :session-id="props.sessionId"
            :page-name="props.pageName"
            @select-file="selectFile"
          />
          <DgrToggleButton v-model="deepMode" label-position="left">
            <div class="deep-research-label c-text-regular--m">
              <DgrIcon
                v-if="canStartNewDeepMode"
                name="deep-research"
                size="small"
              />
              <DgrLoading v-else class="loading-spinner" />
              ディープリサーチ
            </div>
          </DgrToggleButton>
        </div>
        <SurveySelectedFilters
          class="c-text-regular--m"
          v-if="docTypes.length > 0 || specifiedFiles.length > 0"
          :enable-delete="true"
          :doc-types
          :search-scope
          :specified-files
          :group-id
          :group-tag-ids
          @delete-tag="deleteTag"
        />
      </div>
      <button
        ref="submitButtonRef"
        class="c-btn--AnewsPrimary"
        :class="{
          disabled: !enableSubmitButton,
          'change-position': shouldButtonChangePosition,
        }"
        :disabled="!enableSubmitButton"
        v-tooltip="
          isDeepModeConcurrencyLimitReached
            ? {
                placement: 'top',
                content: DEEP_MODE_MAX_CONCURRENCY_MESSAGE,
              }
            : undefined
        "
        @click="sendInput"
      >
        <DgrIcon
          size="small"
          :keep-fill="false"
          name="sparkles-fill"
          class="ai-icon"
        />生成
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.container {
  box-sizing: border-box;
  margin-top: 16px;
  width: 100%;
  background-color: $color-white;
  border-radius: 4px;
  padding: 16px;

  // チャット形式のUIの画面では、入力欄を画面下部に固定する
  &.fixed-bottom {
    position: fixed;
    bottom: 16px;
    // NOTE: 1280px以下の改造だと、本番環境はZendeskのサポートと要約の生成ボタンが被ってしまうので暫定対応
    // 912px（デザイン変更で拡張したサイズ） → 656px（変更前の元サイズ）に変更
    // チケット: https://anews.atlassian.net/browse/ANDEV-7552
    max-width: 656px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    z-index: 1;
    // レスポンシブ対応
    width: -webkit-fill-available;
    width: -moz-available;
    margin-right: 32px;
  }

  .filter-submit-section {
    display: grid;
    grid-template-columns: auto auto;
    gap: 8px;
    place-items: center;
    justify-content: space-between;

    .search-options {
      display: flex;
      gap: 8px;
      align-items: center;
    }

    .deep-research-label {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .loading-spinner {
      width: 24px;
      height: 24px;
    }

    button {
      padding: 8px 16px;
      cursor: pointer;
      align-self: flex-end;
      flex-shrink: 0;

      // NOTE: チャット形式のUIの画面では、入力欄を画面下部に固定する影響で画面縦幅が狭くなる
      // そのため、入力欄が空 かつ textareaにフォーカスがない場合は、入力欄を縮める ("生成"ボタンはtextareaの1行目の位置に配置)
      &.change-position {
        position: absolute;
        top: 16px;
        right: 16px;
      }
    }

    .ai-icon {
      margin-right: 3px;
    }
  }

  .input-section {
    padding-bottom: 8px;

    &.no-filters {
      padding-bottom: 0;
    }

    textarea {
      width: 100%;
      outline: none;
      border: none;
      resize: none;
      min-height: 1.5em;
      max-height: 300px;
      overflow-y: auto;
      padding: 8px;
      box-sizing: border-box;
    }

    ::placeholder {
      color: $color-gray600;
    }
  }

  .input-footer {
    display: flex;
    margin-top: 8px;
    padding: 0px 8px;
  }

  .character-count {
    margin-left: auto;
  }

  .validation-error-message {
    color: $color-orange1000;
  }

  // 高さをなくしてこのエリア自体ないように表示しつつ、元のスタイルを上書きしてボタンの位置を調整
  .hidden-filter-submit-section {
    height: 0;
    padding: 0;
    overflow: hidden;
  }
}
</style>
